/* eslint-disable max-lines */

import React, { useEffect } from "react";
import { hydrateRoot } from "react-dom/client";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { runSaga } from "redux-saga";

import {
  AnyPartnerConfiguration,
  CookiesContext,
  DispatchAction,
  getFeatureGates,
  getMarketInfo,
  MarketInfo,
  ResourceStrings,
} from "@gamedev-web/gamedev-sdk-core";
import { loadableReady } from "@loadable/component";

import { getPathWithoutLocale } from "../helpers";
import { makeResourceStringContextProviderAsync } from "../localization/makeResourceStringContextProviderAsync";
import { PartnerConfigManager } from "../partners/PartnerConfigManager";
import { PartnerContextProvider } from "../partners/PartnerContextProvider";
import { appContextActions } from "../redux";
import { createSagaContext } from "../redux/sagas/helpers/sagaContext";
import { runSagas } from "../redux/sagas/rootSaga";
import { getMatchedRouteConfig } from "../routes";
import { ClientCookiesProvider } from "./ClientCookiesProvider";
import { configureSagaMiddleware, initializeClientReduxStore } from "./reduxBootstrap";

export type InitializeClientOptions = {
  app: () => JSX.Element;
  partnerConfigurations: AnyPartnerConfiguration[];
  /**
   * @deprecated Inject strings via partner packages lazyConfig.
   */
  getResourceStringsAsync?: (marketInfo: MarketInfo) => Promise<ResourceStrings>;
  clientRelyingParties?: string[];
  requiredCookieRelyingParties?: string[];
};

export const initializeClient = (options: InitializeClientOptions) => {
  loadableReady().then(async () => {
    const partnerConfigManager = new PartnerConfigManager();
    partnerConfigManager.setPartnerPackages(options.partnerConfigurations);

    const { store } = initializeClientReduxStore();

    const dispatch = store.dispatch as DispatchAction;
    dispatch(appContextActions.setIsReactDomHydrating(true));

    const state = store.getState();
    const marketInfo = getMarketInfo(state);
    const featureGates = getFeatureGates(state);

    const cookiesProvider = new ClientCookiesProvider();

    const locale = marketInfo.localeWithScript;

    const sagaContext = createSagaContext(cookiesProvider, null, partnerConfigManager);

    const sagaMiddleware = configureSagaMiddleware(sagaContext);
    const pathWithoutLocale = getPathWithoutLocale(window.location.pathname, locale);

    partnerConfigManager.setSagaMiddleware(sagaMiddleware);
    partnerConfigManager.setStore(store);
    await partnerConfigManager.initialize();
    await partnerConfigManager.loadConfigurationForPath(pathWithoutLocale);

    const matchedRouteConfig = getMatchedRouteConfig(pathWithoutLocale, featureGates, locale);

    if (matchedRouteConfig && matchedRouteConfig.lazyConfig) {
      const { bootstrapClientEnvironmentSaga } = await matchedRouteConfig.lazyConfig();

      // `bootstrapClientEnvironmentSaga` is only meant to be called
      // for the first loaded partner config. It is meant to bootstrap the environment
      // before the initial render so as to match what is produced by the server.
      if (bootstrapClientEnvironmentSaga) {
        // use try-catch to make sure we don't fail completely if the bootstrapClientEnvironmentSaga throws an error
        try {
          await sagaMiddleware.run(bootstrapClientEnvironmentSaga).toPromise();
        } catch (error) {
          if (error instanceof Error) {
            console.error("bootstrapClientEnvironmentSaga error", error);
          } else {
            console.error("unknown bootstrapClientEnvironmentSaga error");
          }
        }
      }
    }

    // Call getState() here because `bootstrapClientEnvironmentSaga` may have updated the state before hydration
    // so we should pass in the latest state ReduxProvider to avoid mismatch
    const serverState = store.getState();

    runSagas(sagaMiddleware);

    const ResourceStringsProvider = await makeResourceStringContextProviderAsync(
      marketInfo,
      partnerConfigManager
    );

    // https://reactjs.org/blog/2022/03/08/react-18-upgrade-guide.html#updates-to-client-rendering-apis
    function AppWithCallbackAfterRender() {
      useEffect(() => {
        dispatch(appContextActions.setIsReactDomHydrating(false));
      }, []);

      return (
        <ReduxProvider store={store} serverState={serverState}>
          <CookiesContext.Provider value={cookiesProvider}>
            <ResourceStringsProvider>
              <BrowserRouter basename={locale}>
                <PartnerContextProvider configManager={partnerConfigManager}>
                  <options.app />
                </PartnerContextProvider>
              </BrowserRouter>
            </ResourceStringsProvider>
          </CookiesContext.Provider>
        </ReduxProvider>
      );
    }

    // Handles the `onRecoverableError` callback for React. It will call the `onRecoverableReactError` callback
    // for the partner config if it exists.
    // React will still throw a errors (like hydration errors), but React will also call
    // the `onRecoverableError` callback to let you know there was a recoverable error.
    async function onRecoverableError(error: unknown) {
      const currentPathWithoutLocale = getPathWithoutLocale(
        window.location.pathname,
        marketInfo.localeWithScript
      );

      const partnerConfig = partnerConfigManager.getPartnerConfigForPath(currentPathWithoutLocale);
      if (!partnerConfig || !partnerConfig.lazyConfig) {
        return;
      }

      try {
        const { onRecoverableReactErrorSaga } = await partnerConfig.lazyConfig();

        if (onRecoverableReactErrorSaga) {
          // Convert the runSaga to a promise so we can catch any errors
          await runSaga(
            {
              getState: store.getState.bind(store),
              dispatch: store.dispatch.bind(store),
              context: sagaContext,
            },
            onRecoverableReactErrorSaga,
            error
          ).toPromise();
        }
      } catch (_error) {}
    }

    const container = document.getElementById("root") as Element;
    hydrateRoot(container, <AppWithCallbackAfterRender />, { onRecoverableError });
  });
};
